import { useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Button, Pressable, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { ApprovalFormSchema } from '../schemas';

import { ApprovalRequestForm } from './ApprovalRequestForm';

import styles from '../SaveChangesModal.module.css';

type SaveChangesModalApprovalProps<T extends ApprovalFormSchema> = {
  form: Pick<UseFormReturn<T>, 'control' | 'formState'>;
  onOpenChange?: (isOpen: boolean) => void;
  formClassName?: string;
  isDisabled?: boolean;
};

export function ApprovalRequestButton<T extends ApprovalFormSchema>({
  form,
  onOpenChange,
  formClassName,
  isDisabled,
}: SaveChangesModalApprovalProps<T>) {
  const [isApprovalFormOpen, setIsApprovalFormOpen] = useState(false);

  // This is used to notify the parent component that the approval form open state has changed
  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isApprovalFormOpen);
    }
  }, [isApprovalFormOpen, onOpenChange]);

  if (isApprovalFormOpen) {
    return <ApprovalRequestForm form={form} className={formClassName} />;
  }

  return (
    <div className={styles.ApprovalRequestButtonContainer}>
      <Button
        aria-label={msg('ui.saveChangesModal.addReviewer')}
        onPress={() => setIsApprovalFormOpen(true)}
        data-test-id="add-reviewer-button"
        isDisabled={isDisabled}
      >
        <Icon name="person-add" slot="start-icon" />
        {msg('ui.saveChangesModal.addReviewer')}
      </Button>
      <TooltipTrigger>
        <Pressable data-test-id="add-reviewer-help">
          <Icon name="help-circle" role="button" aria-label={msg('ui.saveChangesModal.addReviewerHelpLabel')} />
        </Pressable>
        <Tooltip>
          <Message id="ui.saveChangesModal.addReviewerHelp" />
        </Tooltip>
      </TooltipTrigger>
    </div>
  );
}
